import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { SCORECARD, SCORECARD_VOCAB } from "@shared/scorecard";
import { Body } from "../../../primitives/data-display/Body";
import { Note } from "../../../primitives/data-display/Note";
import { Meta } from "../../../primitives/data-display/Meta";
import { Panel } from "../../../primitives/surfaces/Panel";
import { gaps } from "../../../primitives/spacing";
import OverrideMode from "./OverrideMode";
import type { FieldCandidateDrafts, MultiScorecardConsensus } from "../../../../lib/corpusApi";
import {
  FieldPanelHeader,
  FieldPanelActions,
  HelpPanel,
  PanelRoot,
  ChipRow,
  ItalicNote,
  type FieldPanelBase,
} from "./panel_shared";

export interface MultiVoterFieldPanelProps extends FieldPanelBase {
  /** Model name → their full-document draft. */
  candidates: Record<string, FieldCandidateDrafts>;
  consensus: MultiScorecardConsensus;
  /** Accept candidate at index `n` (0-indexed, matching keyboard digit - 1). */
  onAcceptCandidate: (candidateIndex: number) => void;
}

const DesktopHint = styled(Stack)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("md")]: { display: "flex" },
}));

const AlignedStartButton = styled(Button)({ alignSelf: "flex-start" });

const ItalicSmallBody = styled(Body)({ fontStyle: "italic", fontSize: "0.8rem" });

/**
 * Multi-candidate annotation panel. Shows one block per model candidate for the active field
 * with a consensus indicator; requires an explicit candidate choice or override — no pre-selection.
 */
export function MultiVoterFieldPanel({
  field,
  candidates,
  consensus,
  override,
  saving,
  helpOpen,
  onAcceptCandidate,
  onOverride,
  onMarkEmpty,
  onReject,
  onHelpToggle,
}: MultiVoterFieldPanelProps) {
  const vocab = SCORECARD_VOCAB[field] ?? [];
  const config = SCORECARD[field];
  const labelDescriptions = config?.labelDescriptions;
  const candidateEntries = Object.entries(candidates);
  const fieldJaccard = consensus.fields[field]?.jaccard ?? null;
  const consensusPct = fieldJaccard !== null ? Math.round(fieldJaccard * 100) : null;

  const consensusBadge =
    consensusPct !== null ? (
      <Tooltip title="Pairwise Jaccard agreement across candidates for this field">
        <Chip
          label={`${consensusPct}% agreement`}
          size="small"
          color={consensusPct >= 70 ? "success" : consensusPct >= 40 ? "warning" : "error"}
        />
      </Tooltip>
    ) : undefined;

  return (
    <PanelRoot gap={gaps.base}>
      <FieldPanelHeader
        field={field}
        badge={consensusBadge}
        helpOpen={helpOpen}
        onHelpToggle={onHelpToggle}
      />

      <div style={{ display: helpOpen ? undefined : "none" }}>
        <HelpPanel
          description={config?.description}
          vocab={vocab}
          labelDescriptions={labelDescriptions}
        />
      </div>

      {override.mode ? (
        <OverrideMode
          labels={vocab}
          labelDescriptions={labelDescriptions}
          selected={override.selection}
          multiple={!config?.singleSelect}
          focusIndex={override.focusIndex}
          onFocusChange={override.onFocusChange}
          onChange={override.onSelectionChange}
          onConfirm={override.onConfirm}
          onCancel={override.onCancel}
        />
      ) : (
        <>
          {candidateEntries.map(([candidateName, candidateDrafts], idx) => {
            const draft = candidateDrafts[field];
            const hasLabels = (draft?.labels.length ?? 0) > 0;
            return (
              <Panel key={candidateName} tone="muted" pad="sm">
                <Stack gap={gaps.tight}>
                  <Meta>{candidateName}</Meta>
                  {hasLabels ? (
                    <ChipRow direction="row" gap={gaps.tight}>
                      {draft!.labels.map((label) => (
                        <Chip key={label} label={label} />
                      ))}
                    </ChipRow>
                  ) : (
                    <ItalicNote>No labels</ItalicNote>
                  )}
                  {draft?.evidence && (
                    <ItalicSmallBody>&ldquo;{draft.evidence}&rdquo;</ItalicSmallBody>
                  )}
                  <AlignedStartButton
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={() => onAcceptCandidate(idx)}
                    disabled={saving || !hasLabels}
                  >
                    {idx + 1} Accept
                  </AlignedStartButton>
                </Stack>
              </Panel>
            );
          })}

          <Divider />

          <FieldPanelActions
            saving={saving}
            onOverride={onOverride}
            onMarkEmpty={onMarkEmpty}
            onReject={onReject}
          />

          <DesktopHint>
            <Note>
              {candidateEntries.map((_, i) => `${i + 1} accept candidate ${i + 1}`).join(" · ")}
              {" · O override · E mark empty · ? help"}
            </Note>
          </DesktopHint>
        </>
      )}
    </PanelRoot>
  );
}
